import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  InvoiceResponse,
  getInvoice,
  markInvoiceReviewed,
  downloadInvoiceExport,
} from '@/services/api/invoice';
import { showErrorToast, showSuccessToast } from '@/components/toast/globalToast';
import { getUserEmail } from '@/utils';
import InvoiceControlsBar from './components/InvoiceControlsBar';
import InvoiceTotalCard from './components/InvoiceTotalCard';
import InvoiceTable from './components/InvoiceTable';
import InvoiceGroupedSummary from './components/InvoiceGroupedSummary';
import InvoiceDataChangedToast from './components/InvoiceDataChangedToast';
import MarkReviewedModal from './components/MarkReviewedModal';

const InvoiceListPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const userEmail = getUserEmail();

  const [selectedOemId, setSelectedOemId] = useState(
    searchParams.get('oem') ?? ''
  );
  const [selectedPeriod, setSelectedPeriod] = useState(
    searchParams.get('period') ?? ''
  );

  const [invoiceData, setInvoiceData] = useState<InvoiceResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [dismissedDelta, setDismissedDelta] = useState(false);
  const [showMarkModal, setShowMarkModal] = useState(false);
  const [isMarkingReviewed, setIsMarkingReviewed] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const fetchInvoice = useCallback(
    async (oemId: string, period: string) => {
      if (!oemId || !period) return;
      setIsLoading(true);
      setDismissedDelta(false);
      try {
        const data = await getInvoice({ oem_id: oemId, period });
        setInvoiceData(data);
      } catch (error: unknown) {
        const msg =
          error instanceof Error ? error.message : 'Something went wrong.';
        showErrorToast('Error', msg);
        setInvoiceData(null);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (selectedOemId) next.set('oem', selectedOemId);
        else next.delete('oem');
        if (selectedPeriod) next.set('period', selectedPeriod);
        else next.delete('period');
        return next;
      },
      { replace: true }
    );
    fetchInvoice(selectedOemId, selectedPeriod);
  }, [selectedOemId, selectedPeriod, fetchInvoice, setSearchParams]);

  const handleMarkReviewed = async () => {
    if (!invoiceData) return;
    setIsMarkingReviewed(true);
    setShowMarkModal(false);

    // Optimistic update — show badge immediately
    const prevData = invoiceData;
    setInvoiceData((prev) =>
      prev ? { ...prev, status: 'REVIEWED' } : prev
    );

    try {
      const result = await markInvoiceReviewed({
        oemId: selectedOemId,
        period: selectedPeriod,
        updatedBy: userEmail,
      });
      // Sync full server response
      setInvoiceData((prev) =>
        prev
          ? {
              ...prev,
              status: result.status,
              reviewedBy: result.reviewedBy,
              reviewedAt: result.reviewedAt,
              reviewedTotal: result.reviewedTotal,
            }
          : prev
      );
      showSuccessToast('Reviewed', 'Period has been marked as reviewed.');
    } catch (error: unknown) {
      // Revert on failure
      setInvoiceData(prevData);
      const msg =
        error instanceof Error ? error.message : 'Something went wrong.';
      showErrorToast('Error', msg);
    } finally {
      setIsMarkingReviewed(false);
    }
  };

  const handleExport = async () => {
    if (!selectedOemId || !selectedPeriod) return;
    setIsExporting(true);
    try {
      const blob = await downloadInvoiceExport({
        oem_id: selectedOemId,
        period: selectedPeriod,
      });
      const oemName = invoiceData?.oem.name.replace(/\s+/g, '_') ?? 'invoice';
      const filename = `invoice_${oemName}_${selectedPeriod}.csv`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      showSuccessToast('Export', 'Download started.');
    } catch (error: unknown) {
      const msg =
        error instanceof Error ? error.message : 'Something went wrong.';
      showErrorToast('Error', msg);
    } finally {
      setIsExporting(false);
    }
  };

  const displayData = invoiceData;
  const showDataChangedToast =
    !dismissedDelta &&
    displayData?.dataChangedDelta !== null &&
    displayData?.dataChangedDelta !== undefined;

  return (
    <div className="bg-white p-6 max-w-full mx-auto">
      <div className="mb-6">
        <h1 className="text-[24px] font-semibold text-gray-900 mb-1">Invoice</h1>
        <p className="text-[14px] text-gray-600">
          Verify OEM billing against FMS vehicle data.
        </p>
      </div>

      <InvoiceControlsBar
        selectedOemId={selectedOemId}
        selectedPeriod={selectedPeriod}
        onOemChange={setSelectedOemId}
        onPeriodChange={setSelectedPeriod}
        onExport={handleExport}
        isLoading={isLoading}
        hasData={!!displayData && displayData.lineItems.length > 0}
        isExporting={isExporting}
      />

      {showDataChangedToast && displayData && (
        <InvoiceDataChangedToast
          delta={displayData.dataChangedDelta!}
          onDismiss={() => setDismissedDelta(true)}
        />
      )}

      {!selectedOemId || !selectedPeriod ? (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <i className="ri-file-list-3-line text-[56px] text-gray-200 mb-4" />
          <p className="text-gray-400 text-sm">
            Select an OEM and billing period to load invoice data.
          </p>
        </div>
      ) : displayData ? (
        <>
          <InvoiceTotalCard
            grandTotal={displayData.grandTotal}
            flaggedCount={displayData.flaggedCount}
            status={displayData.status}
            reviewedBy={displayData.reviewedBy}
            reviewedAt={displayData.reviewedAt}
            onMarkReviewed={() => setShowMarkModal(true)}
            isMarkingReviewed={isMarkingReviewed}
          />

          <InvoiceTable
            lineItems={displayData.lineItems}
            isLoading={false}
            oemName={displayData.oem.name}
            period={displayData.period}
          />

          <InvoiceGroupedSummary rows={displayData.groupedSummary} />
        </>
      ) : (
        <InvoiceTable
          lineItems={[]}
          isLoading={isLoading}
          oemName=""
          period=""
        />
      )}

      <MarkReviewedModal
        isOpen={showMarkModal}
        oemName={displayData?.oem.name ?? ''}
        period={selectedPeriod}
        grandTotal={displayData?.grandTotal ?? 0}
        onConfirm={handleMarkReviewed}
        onCancel={() => setShowMarkModal(false)}
        isLoading={isMarkingReviewed}
      />
    </div>
  );
};

export default InvoiceListPage;
